import React, { useEffect } from "react";
import { Helmet } from "react-helmet";
import NewFooter from "../comp/NewFooter";

export default function PixGrid() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>PixMate: Photo Collage Maker | Privacy Policy</title>
      </Helmet>
      <nav className="flex flex-col items-center md:flex-row md:justify-center gap-5 py-5">
        <img
          src="https://rareprob.com/images/icons/PixGrid.png"
          alt="PixMate: Photo Collage Maker"
          className="w-24 h-24 md:w-32 md:h-32"
        />
        <h1 className="mt-5 md:mt-10 font-bold text-3xl md:text-5xl text-gray-800 text-center">
          PixMate: Photo Collage Maker
        </h1>
      </nav>
      <main>
        <div className="w-5/6 md:w-3/4 px-1 break-words md:px-0 m-auto text-gray-700 text-xl">
          <h1 className="mt-20 font-bold text-5xl text-gray-800">
            Privacy Policy
          </h1>
          <p className="mt-4 text-gray-500 text-base">Last Updated: May 27, 2026</p>

          <p className="mt-6">
            PixMate: Photo Collage Maker is developed by ASD Dev Video Player for All Format. We are committed to protecting your privacy and being transparent about how your data is collected, used, and shared. Please read this policy carefully before using the app.
          </p>

          {/* Table of Contents */}
          <div className="mt-10 bg-gray-50 border border-gray-200 rounded-xl p-6">
            <h2 className="font-bold text-2xl text-gray-800 mb-4">Overview of Privacy Policy</h2>
            <ol className="list-decimal list-inside space-y-2 text-blue-600 text-base">
              <li><a href="#section-1" className="hover:underline">Definitions</a></li>
              <li><a href="#section-2" className="hover:underline">Information We Collect</a></li>
              <li><a href="#section-3" className="hover:underline">Permissions We Use</a></li>
              <li><a href="#section-4" className="hover:underline">How We Use Your Data</a></li>
              <li><a href="#section-5" className="hover:underline">Share, Transfer, and Public Disclosure of User&apos;s Personal Information</a></li>
              <li><a href="#section-6" className="hover:underline">Storage and Protection of Users&apos; Personal Information</a></li>
              <li><a href="#section-7" className="hover:underline">Use of Cookies</a></li>
              <li><a href="#section-8" className="hover:underline">Data Protection Officer (DPO)</a></li>
              <li><a href="#section-9" className="hover:underline">Your Privacy Rights and Choices</a></li>
              <li><a href="#section-10" className="hover:underline">Users of California</a></li>
              <li><a href="#section-11" className="hover:underline">EU Users</a></li>
              <li><a href="#section-12" className="hover:underline">Users of Brazil</a></li>
              <li><a href="#section-13" className="hover:underline">Users of Virginia</a></li>
              <li>
                <a href="#section-14" className="hover:underline">DMCA Copyright Policy</a>
                <ol className="list-disc list-inside ml-6 mt-1 space-y-1 text-blue-500 text-sm">
                  <li><a href="#section-14-1" className="hover:underline">Infringement Policy</a></li>
                  <li><a href="#section-14-1-1" className="hover:underline">How to Report Infringement</a></li>
                </ol>
              </li>
              <li><a href="#section-15" className="hover:underline">In-app Purchases</a></li>
              <li><a href="#section-16" className="hover:underline">Liability Limitation</a></li>
              <li><a href="#section-17" className="hover:underline">Disclaimer of Warranty</a></li>
              <li><a href="#section-18" className="hover:underline">Copyright and Trademarks</a></li>
              <li><a href="#section-19" className="hover:underline">Push Notifications</a></li>
              <li><a href="#section-20" className="hover:underline">Retention of data</a></li>
              <li><a href="#section-21" className="hover:underline">Data Recovery Policy</a></li>
              <li><a href="#section-22" className="hover:underline">Security of data</a></li>
              <li><a href="#section-23" className="hover:underline">Our Policy On "DO NOT TRACK" Signals Under The California Online Protection Act (CALOPPA)</a></li>
              <li>
                <a href="#section-24" className="hover:underline">Third-Party Services</a>
                <ol className="list-decimal list-inside ml-6 mt-1 space-y-1 text-blue-500 text-sm">
                  <li>
                    <a href="#section-24-1" className="hover:underline">Disclaimer</a>
                    <ol className="list-disc list-inside ml-6 mt-1 space-y-1 text-blue-400 text-sm">
                      <li><a href="#section-24-1-1" className="hover:underline">Analytics</a></li>
                      <li><a href="#section-24-1-2" className="hover:underline">Advertising</a></li>
                      <li><a href="#section-24-1-3" className="hover:underline">Behavioral Remarketing</a></li>
                    </ol>
                  </li>
                </ol>
              </li>
              <li><a href="#section-25" className="hover:underline">Payments</a></li>
              <li><a href="#section-26" className="hover:underline">Links to other sites</a></li>
              <li><a href="#section-27" className="hover:underline">Children&apos;s Privacy</a></li>
              <li><a href="#section-28" className="hover:underline">Privacy Policy Changes</a></li>
              <li><a href="#section-29" className="hover:underline">Contact Us</a></li>
            </ol>
          </div>

          {/* Section 1 - Definitions */}
          <section id="section-1" className="mt-12">
            <h2 className="font-bold text-3xl text-gray-800 mb-4">Definitions</h2>
            <p className="mb-4">We have added this part so that users can easily understand the basics of the privacy policy.</p>

            <div className="ml-2 space-y-6">
              <div>
                <h3 className="font-semibold text-xl text-gray-800 mb-2">Service</h3>
                <p>Service means games, mobile applications, other products ("Games"), and websites operated by ASD Dev Video Player for All Format ("we," "our," or "us").</p>
              </div>
              <div>
                <h3 className="font-semibold text-xl text-gray-800 mb-2">Personal Data</h3>
                <p>Personal Data means data about a living individual who can be identified from that data (or from those and other information either in our possession or likely to come into our possession).</p>
              </div>
              <div>
                <h3 className="font-semibold text-xl text-gray-800 mb-2">Usage Data</h3>
                <p>Usage Data is data collected automatically, either generated by the use of the Service or from the Service infrastructure itself (for example, the duration of a page visit).</p>
              </div>
              <div>
                <h3 className="font-semibold text-xl text-gray-800 mb-2">Cookies</h3>
                <p>Cookies are small files stored on your device (computer or mobile device).</p>
              </div>
              <div>
                <h3 className="font-semibold text-xl text-gray-800 mb-2">Data Controller</h3>
                <p>Data Controller means the natural or legal person who (either alone or jointly or in common with other persons) determines the purposes for which and how any personal information is, or is to be, processed. For this Privacy Policy, we are a Data Controller of your Personal Data.</p>
              </div>
              <div>
                <h3 className="font-semibold text-xl text-gray-800 mb-2">Data Processors (or Service Providers)</h3>
                <p>Data Processor (or Service Provider) means any natural or legal person who processes the data on behalf of the Data Controller. We may use the services of various Service Providers to process your data more effectively.</p>
              </div>
              <div>
                <h3 className="font-semibold text-xl text-gray-800 mb-2">Data Subject (or User)</h3>
                <p>Data Subject is any living individual who is using our Service and is the subject of Personal Data.</p>
              </div>
              <div>
                <h3 className="font-semibold text-xl text-gray-800 mb-2">Data Protection Officer (DPO)</h3>
                <p>A DPO is a designated person who is in charge of managing an organization's or company's data protection policy and making sure that data protection rules and laws, like the General Data Protection Regulation (GDPR) in the European Union, are followed. The DPO plays a critical role in safeguarding individuals' rights to privacy by making sure that personal data is handled safely and in compliance with the law.</p>
              </div>
              <div>
                <h3 className="font-semibold text-xl text-gray-800 mb-2">Advertising ID</h3>
                <p>AAID (Android) or IDFA (iOS) — a resettable device identifier used for advertising.</p>
              </div>
            </div>
          </section>

          {/* Section 2 - Information We Collect */}
          <section id="section-2" className="mt-12">
            <h2 className="font-bold text-3xl text-gray-800 mb-4">
              Information We Collect
            </h2>
            <p className="mb-4">
              We collect limited information to provide and improve our services.
            </p>

            <div className="ml-2 space-y-6">
              <div>
                <h3 className="font-semibold text-xl text-gray-800 mb-2">1. Information Collected Automatically</h3>
                <p className="mb-2">When you use the app, we may collect:</p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>Device information (such as device model and operating system version)</li>
                  <li>App usage data (such as features used and session duration)</li>
                  <li>Crash logs and diagnostic information</li>
                  <li>Advertising identifiers (such as AAID/IDFA)</li>
                  <li>IP address (used for analytics, fraud prevention, and approximate location)</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-xl text-gray-800 mb-2">2. Information You Provide</h3>
                <p className="mb-2">We may collect:</p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>Email address (only if you contact us for support)</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-xl text-gray-800 mb-2">3. Information from Third-Party Services</h3>
                <p className="mb-2">We use third-party services for analytics, advertising, and app functionality. These services may collect:</p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>Device identifiers</li>
                  <li>Advertising ID</li>
                  <li>App interaction and performance data</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-xl text-gray-800 mb-2">4. Data We Do NOT Collect</h3>
                <p>
                  We do not ask for or collect any Sensitive Personal Data or information such as passwords related to other services, financial information such as bank account or credit card, debit card, or other payment instrument details, physical, physiological, and mental health conditions, sexual orientation, medical records, and history, or biometric information.
                </p>
              </div>
            </div>
          </section>

          {/* Section 3 - Permissions We Use */}
          <section id="section-3" className="mt-12">
            <h2 className="font-bold text-3xl text-gray-800 mb-4">Permissions We Use</h2>
            <p className="mb-4">The different types of permissions we ask from users to offer the best of our products and services are:</p>

            <div className="ml-2 space-y-6">
              <div>
                <h3 className="font-semibold text-xl text-gray-800 mb-2">1. Local Media Permission</h3>
                <p className="mb-2">
                  This permission allows the app to access photos and media files on your device so you can select images for editing and collage creation. All media processing happens locally, and your files are not uploaded.
                </p>
                <p>
                  On devices running Android 13 and above, the app requests READ_MEDIA_IMAGES and READ_MEDIA_VIDEO permissions to access photos and videos you choose to edit or include in collages. On Android 14 and above, the app may use the READ_MEDIA_VISUAL_USER_SELECTED permission, which allows you to grant access only to specific images and videos you select, rather than your entire media library. These permissions are used solely within the app and no media files are uploaded to external servers.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-xl text-gray-800 mb-2">2. Network</h3>
                <p>
                  This permission allows the app to connect to the internet for advertising, analytics, and feature updates. All image editing and media processing are performed locally on your device.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-xl text-gray-800 mb-2">3. Location Information</h3>
                <p>
                  We do not collect or access your precise location. Approximate location may be inferred from your IP address by third-party services for analytics and advertising purposes.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-xl text-gray-800 mb-2">4. Usage and Tracking Data</h3>
                <p>
                  This Usage Data may include information such as your computer's Internet Protocol address (e.g. IP address), browser type, browser version, the pages of our Service that you visit, the time and date of your visit, the time spent on those pages, unique device identifiers and other diagnostic data. It may also include information such as the type of mobile device you use, your mobile device's unique ID, the IP address of your mobile device, your mobile operating system, the type of mobile Internet browser you use, unique device identifiers, usage patterns, and other diagnostic data. We collect non-personal tracking information to help us improve the App and provide better services.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-xl text-gray-800 mb-2">5. Consumer Support and Follow-up Services</h3>
                <p>
                  When users send their feedback or error reports via email, we obtain your username, email address, and email content. This helps in communicating with you about any problems you face while using our product or service.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-xl text-gray-800 mb-2">6. Notification Permission</h3>
                <p>
                  For devices with Android 13 and above, with your permission, we may send notifications to inform you of important events, messages, news, or updates. You can turn these notifications on or off at any time in your device settings. We do not use notifications for unsolicited advertising.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-xl text-gray-800 mb-2">7. Crash Logs &amp; Diagnostics</h3>
                <p>
                  To improve stability and identify bugs, our app may collect anonymized crash logs and diagnostic reports. These logs do not include personal content (such as photos, videos, or private files) and are used solely for troubleshooting and performance optimization.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-xl text-gray-800 mb-2">8. Storage Permission</h3>
                <p className="mb-2">
                  The app requests access to your device's storage in order to read, modify, or delete media files as per your actions inside the app.
                </p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>The app requests MANAGE_EXTERNAL_STORAGE only to let users play, organize, clean, and secure their media files.</li>
                  <li>All operations are performed locally on the device and no media files are uploaded or shared externally.</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-xl text-gray-800 mb-2">9. Biometric Authentication</h3>
                <p className="mb-2">
                  Our app may use biometric authentication features available on your device (such as fingerprint) to protect access to your private vault.
                </p>
                <p>
                  We do not collect, store, or share your biometric data. This authentication is handled entirely by your device's operating system. We only receive a confirmation of successful authentication to grant access.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-xl text-gray-800 mb-2">10. Wake Lock Permission</h3>
                <p>
                  Our app uses this to keep your device awake temporarily while performing essential tasks such as processing images, playing videos, or completing ongoing operations. This permission does not collect or share any personal data and is used solely to ensure smooth app functionality.
                </p>
              </div>
            </div>
          </section>

          {/* Section 4 - How We Use Your Data */}
          <section id="section-4" className="mt-12">
            <h2 className="font-bold text-3xl text-gray-800 mb-4">How We Use Your Data</h2>
            <p className="mb-2">We use the collected data for various purposes:</p>
            <ul className="list-disc list-inside ml-4 space-y-1 mb-4">
              <li>To provide and maintain our Service</li>
              <li>To notify you about changes to our Service</li>
              <li>To allow you to participate in interactive features of our Service when you choose to do so</li>
              <li>To provide customer support</li>
              <li>To gather analysis or valuable information so that we can improve our Service</li>
              <li>To monitor the usage of our Service</li>
              <li>To detect, prevent, and address technical issues</li>
              <li>To follow legal guidelines</li>
              <li>To identify and resolve bugs by analyzing crash logs and diagnostic reports</li>
            </ul>
          </section>

          {/* Section 5 - Share, Transfer, Public Disclosure */}
          <section id="section-5" className="mt-12">
            <h2 className="font-bold text-3xl text-gray-800 mb-4">
              Share, Transfer and Public Disclosure of User&apos;s Personal Information
            </h2>

            <h3 className="font-semibold text-xl text-gray-800 mb-2">SHARE</h3>
            <p className="mb-4">
              Your privacy is our priority. We may share limited user data with trusted third-party service providers such as Google Firebase to facilitate analytics, app performance monitoring, and service improvement. These third parties process data on our behalf in accordance with their respective privacy policies.
            </p>
            <ul className="list-disc list-inside ml-4 space-y-1 mb-4">
              <li>Obtained your permission</li>
              <li>When needed to comply with state agencies, authority, or administrative or legal instructions</li>
              <li>To meet the objectives stated in &apos;Collection and Use of Users&apos; Personal Information&apos;</li>
              <li>Share with ad services and tech support partners</li>
            </ul>
            <p className="mb-4">A list of available third-party SDKs for integration is mentioned below under the "Service Providers".</p>

            <h3 className="font-semibold text-xl text-gray-800 mb-2">TRANSFER</h3>
            <p className="mb-4">
              We will not disclose your personal information to any company, organization, or individual, except as required by law, administrative enforcement, or regulatory authorities.
            </p>

            <h3 className="font-semibold text-xl text-gray-800 mb-2">PUBLIC DISCLOSURE</h3>
            <p className="mb-4">
              As required by laws, regulations, or legal authorities, we may need to share your personal information with enforcement or regulatory bodies. Any disclosure will be limited to the specific information requested and will follow the prescribed legal procedures.
            </p>
          </section>

          {/* Section 6 - Storage and Protection */}
          <section id="section-6" className="mt-12">
            <h2 className="font-bold text-3xl text-gray-800 mb-4">
              Storage and Protection of Users&apos; Personal Information
            </h2>
            <p className="mb-4">
              Our app requires access to your device's media storage to provide seamless functionality, such as browsing, playing, downloading, and managing your media files. This access is granted through the{" "}
              <a href="https://developer.android.com/training/data-storage/shared/media" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Media Storage Permission</a>{" "}
              as per Google's guidelines. By allowing this permission, our app can retrieve and display media files stored on your device, such as videos, music, and any format of media files to enhance your user experience. Rest assured, we only access the necessary media files for the app's core functions, and we do not collect, store, share, or sell any personal information from your media library. Users retain full control over their files, and data is handled responsibly in compliance with Google's policies to ensure privacy and security.
            </p>
            <p className="mb-4">
              For AI-powered features, any processing of user images occurs either locally on your device or via encrypted channels. Processed images are not retained on our servers beyond the session. Crash logs and diagnostics are anonymized before storage, ensuring no personal data can be linked back to an individual.
            </p>

            <h3 className="font-semibold text-xl text-gray-800 mb-2">Local HTTP Server (NanoHTTPD)</h3>
            <p className="mb-2">
              Our app uses NanoHTTPD, a lightweight open-source library, to create a local HTTP server environment on your device. This server operates entirely on your device (localhost) and is used solely for the offline gaming feature.
            </p>
            <ul className="list-disc list-inside ml-4 space-y-1">
              <li>The local server does not transmit any data to external servers or third parties.</li>
              <li>It does not accept connections from other devices on your network.</li>
              <li>It is only active when offline gaming is in use and does not run persistently in the background.</li>
              <li>No personal data, media files, or user information is served through or exposed by this local server.</li>
            </ul>
          </section>

          {/* Section 7 - Use of Cookies */}
          <section id="section-7" className="mt-12">
            <h2 className="font-bold text-3xl text-gray-800 mb-4">Use of Cookies</h2>
            <p className="mb-4">
              We value your privacy and treat data collected through cookies and similar technologies as non-personal information. A "cookie" is a small piece of data stored on your device by websites to help enhance your browsing experience. These cookies do not contain personal details such as your name, email address, or any other identifiable information. Instead, they are used to recognize your device and remember preferences or actions taken during your visit. You have the option to disable cookies at any time through your browser settings.
            </p>
            <p className="mb-2">Examples of Cookies we use:</p>
            <div className="ml-2 space-y-4">
              <div>
                <h3 className="font-semibold text-xl text-gray-800 mb-1">Session Cookies</h3>
                <p>We use Session Cookies to operate our Service.</p>
              </div>
              <div>
                <h3 className="font-semibold text-xl text-gray-800 mb-1">Preference Cookies</h3>
                <p>We use Preference Cookies to remember your preferences and various settings.</p>
              </div>
              <div>
                <h3 className="font-semibold text-xl text-gray-800 mb-1">Security Cookies</h3>
                <p>We use Security Cookies for security purposes.</p>
              </div>
              <div>
                <h3 className="font-semibold text-xl text-gray-800 mb-1">Advertising Cookies</h3>
                <p>Advertising Cookies are used to serve you with advertisements that may be relevant to you and your interests.</p>
              </div>
            </div>
          </section>

          {/* Section 8 - DPO */}
          <section id="section-8" className="mt-12">
            <h2 className="font-bold text-3xl text-gray-800 mb-4">Data Protection Officer (DPO)</h2>
            <p className="mb-4">
              We have appointed a Data Protection Officer (DPO) to oversee our data protection strategy and ensure that our app complies with privacy regulations. The DPO is responsible for:
            </p>
            <ul className="list-disc list-inside ml-4 space-y-1 mb-4">
              <li>Monitoring compliance with data protection laws.</li>
              <li>Acting as a point of contact for data protection-related inquiries.</li>
              <li>Assisting users in exercising their data protection rights.</li>
            </ul>
            <p className="mb-2">If you have any questions about your privacy or would like to exercise any of your rights under applicable laws, please contact our DPO.</p>
            <p className="mb-1 font-bold">Contact Information for the DPO:</p>
            <p>
              Email:{" "}
              <a href="mailto:feedback@rareprob.com" className="text-blue-600 hover:underline">
                feedback@rareprob.com
              </a>
            </p>
            <p>Contact No.: +91 8279323413</p>
          </section>

          {/* Section 9 - Your Privacy Rights and Choices */}
          <section id="section-9" className="mt-12">
            <h2 className="font-bold text-3xl text-gray-800 mb-4">Your Privacy Rights and Choices</h2>
            <p className="mb-2">
              Depending on the laws in your jurisdiction, you may have several rights regarding your personal information. These rights could include:
            </p>
            <ul className="list-disc list-inside ml-4 space-y-1 mb-4">
              <li>Access to Information: You can request information about the data we have collected about you.</li>
              <li>Manage Permissions: You may revoke any permissions you&apos;ve previously granted, such as access to your camera or photo library.</li>
              <li>Data Deletion: Request the deletion of your personal information.</li>
              <li>Opt-Out: You can choose to opt out of receiving personalized advertisements.</li>
              <li>You can opt out of third-party login and use email/password instead.</li>
              <li>You may request access, correction, or deletion of your personal data.</li>
              <li>You may disable account tracking or data collection for analytics in Settings.</li>
            </ul>

            <h3 className="font-semibold text-2xl text-gray-800 mt-8 mb-3">Opt-Out of Data Sharing and Targeted Advertising</h3>
            <p className="mb-4">
              We respect our users&apos; privacy and provide them with control over how their data is used. We do not sell personal information of children under any circumstances. For general users, we do not use personal data for targeted or behavioral advertising unless explicitly stated.
            </p>
            <p className="mb-2">You have the right to opt out of the sharing of your data for advertising or analytics purposes. You can exercise this right by:</p>
            <ul className="list-disc list-inside ml-4 space-y-1 mb-4">
              <li>Disabling Ad Personalization in your device settings</li>
              <li>Resetting or limiting your Advertising ID (AAID/IDFA)</li>
              <li>
                Contact us at{" "}
                <a href="mailto:feedback@rareprob.com" className="text-blue-600 hover:underline">
                  feedback@rareprob.com
                </a>{" "}
                with the subject: &quot;Opt-Out Request&quot;
              </li>
            </ul>
            <p className="mb-4">Once we receive your request, we will process it within a reasonable timeframe in accordance with applicable laws such as GDPR, CCPA, and other privacy regulations.</p>

            <h3 className="font-semibold text-2xl text-gray-800 mb-3">Right to Lodge a Complaint</h3>
            <p className="mb-4">
              You have the right to lodge a complaint with a competent supervisory authority if you believe that your data protection rights have been violated.
            </p>
            <p className="mb-4">
              For users in the European Economic Area (EEA), you may contact your local data protection authority. A list of supervisory authorities is available through your country&apos;s official data protection body.
            </p>
            <p className="mb-4">
              For users in other regions, you may contact your relevant regulatory authority or data protection authority in your jurisdiction.
            </p>
            <p>
              We encourage you to contact us first at{" "}
              <a href="mailto:feedback@rareprob.com" className="text-blue-600 hover:underline">
                feedback@rareprob.com
              </a>
              , and we will do our best to address your concerns promptly and transparently.
            </p>
          </section>

          {/* Section 10 - California */}
          <section id="section-10" className="mt-12">
            <h2 className="font-bold text-3xl text-gray-800 mb-4">Users of California</h2>
            <p className="mb-2">
              If you are a California resident, you have the following rights according to CCPA:
            </p>
            <ul className="list-disc list-inside ml-4 space-y-1 mb-4">
              <li>Right to Know: Users have the right to know about how their personal information is being used or shared.</li>
              <li>Right to Access: Request information about the personal data we have collected about you.</li>
              <li>Right to Delete: Request deletion of your personal data.</li>
              <li>Right to Opt-Out: Opt-out of the sale of your personal data (Note: We do not sell personal data).</li>
            </ul>
            <p>
              To exercise these rights, please contact us at{" "}
              <a href="mailto:feedback@rareprob.com" className="text-blue-600 hover:underline">
                feedback@rareprob.com
              </a>
              .
            </p>
          </section>

          {/* Section 11 - EU Users */}
          <section id="section-11" className="mt-12">
            <h2 className="font-bold text-3xl text-gray-800 mb-4">EU Users</h2>
            <p className="mb-2">
              For EU users, under the GDPR, you have the right to:
            </p>
            <ul className="list-disc list-inside ml-4 space-y-1 mb-4">
              <li>Right to Access: Request access to your personal data and obtain a copy.</li>
              <li>Right to Rectification: Request correction of inaccurate personal data.</li>
              <li>Right to Erasure: Request deletion of your personal data.</li>
              <li>Right to Restrict Processing: Request restriction of processing your personal data.</li>
              <li>Right to Data Portability: Request transfer of your data to another organization.</li>
              <li>Right to Object: Object to the processing of your personal data.</li>
            </ul>
            <p>
              To exercise these rights, please contact us at{" "}
              <a href="mailto:feedback@rareprob.com" className="text-blue-600 hover:underline">
                feedback@rareprob.com
              </a>
              .
            </p>
          </section>

          {/* Section 12 - Brazil */}
          <section id="section-12" className="mt-12">
            <h2 className="font-bold text-3xl text-gray-800 mb-4">Users of Brazil</h2>
            <p className="mb-2">
              If you are in Brazil, you have the following rights under the LGPD:
            </p>
            <ul className="list-disc list-inside ml-4 space-y-1 mb-4">
              <li>Right to Confirmation: Confirm the existence of processing of your personal data.</li>
              <li>Right to Access: Access your personal data.</li>
              <li>Right to Correction: Correct incomplete, inaccurate, or outdated data.</li>
              <li>Right to Deletion: Request deletion of unnecessary or excessive data.</li>
              <li>Right to Data Portability: Request transfer of your data to another service provider.</li>
              <li>Right to Opposition: Oppose the processing of your personal data in certain situations.</li>
            </ul>
            <p>
              To exercise these rights, please contact us at{" "}
              <a href="mailto:feedback@rareprob.com" className="text-blue-600 hover:underline">
                feedback@rareprob.com
              </a>
              .
            </p>
          </section>

          {/* Section 13 - Virginia */}
          <section id="section-13" className="mt-12">
            <h2 className="font-bold text-3xl text-gray-800 mb-4">Users of Virginia</h2>
            <p className="mb-4">
              For residents of Virginia, they have certain rights under the Virginia Consumer Data Protection Act (VCDPA), including the right to access, delete, and opt out of specific types of data processing.
            </p>

            <h3 className="font-semibold text-xl text-gray-800 mb-2">How We Use Data</h3>
            <p className="mb-2">We may access and process certain data, like:</p>
            <ul className="list-disc list-inside ml-4 space-y-1 mb-4">
              <li>Files and media stored on your device (only to provide core file management features)</li>
              <li>Device identifiers (such as Advertising ID)</li>
              <li>App usage data for analytics and performance improvement</li>
            </ul>
            <p className="mb-4">We do not sell your personal data. However, we may use trusted third-party services (such as analytics and advertising providers) that may process limited data for personalized advertising or app performance analysis.</p>

            <h3 className="font-semibold text-xl text-gray-800 mb-2">Your Right to Opt-Out</h3>
            <p className="mb-2">You have the right to opt out of:</p>
            <ul className="list-disc list-inside ml-4 space-y-1 mb-4">
              <li>Targeted advertising</li>
              <li>The processing of personal data for analytics (where applicable)</li>
              <li>Profiling in furtherance of decisions that produce legal or similarly significant effects</li>
            </ul>
            <p className="mb-2">You can exercise your opt-out rights through the following methods:</p>
            <ul className="list-disc list-inside ml-4 space-y-2 mb-4">
              <li>
                <span className="font-medium">In-App Controls:</span> If available, you can manage your privacy preferences directly within the app settings, including disabling personalized ads.
              </li>
              <li>
                <span className="font-medium">Device-Level Controls:</span> You can limit data used for advertising by disabling ad personalization in your device settings or resetting or deleting your Advertising ID (Android/iOS).
              </li>
              <li>
                <span className="font-medium">Contact Us:</span> You may submit a request to opt out by contacting us at{" "}
                <a href="mailto:feedback@rareprob.com" className="text-blue-600 hover:underline">
                  feedback@rareprob.com
                </a>
                .
              </li>
            </ul>
            <p>
              We will review and respond to your request within the timeframe required by applicable law. We do not discriminate against you for exercising your privacy rights.
            </p>
          </section>

          {/* Section 14 - DMCA */}
          <section id="section-14" className="mt-12">
            <h2 className="font-bold text-3xl text-gray-800 mb-4">DMCA Copyright Policy</h2>
            <p className="mb-4">
              All Content available on the Platform is licensed from third-party services such as YouTube. All copyrights are owned by their respective owners and are used with permission. The Platform only provides outbound links to these Third-Party Services and has no direct control over their content. In compliance with the Digital Millennium Copyright Act, the Company has adopted the following general stance toward copyright infringement.
            </p>
            <p className="mb-4">
              YouTube Content: third-party content available through the use of Google&apos;s API (such as text, images, videos, audio, or software) hosted by YouTube.
            </p>

            <h3 id="section-14-1" className="font-semibold text-xl text-gray-800 mb-2">Infringement Policy</h3>
            <p className="mb-4">
              We respect the intellectual property rights of others and expect our users to do the same. If you believe that your copyrighted work or intellectual property has been used in a way that constitutes infringement within our app, please notify us immediately.
            </p>

            <h3 id="section-14-1-1" className="font-semibold text-xl text-gray-800 mb-2">How to Report Infringement</h3>
            <p className="mb-2">If you believe your rights have been violated, please provide us with the following information:</p>
            <ul className="list-disc list-inside ml-4 space-y-1 mb-4">
              <li>A description of the copyrighted or intellectual property that you claim has been infringed.</li>
              <li>A description of where the material in question is located within the app (such as a link or screenshot).</li>
              <li>Your contact information, including your name, email address, and phone number.</li>
              <li>A statement that you have a good faith belief that the use of the material is not authorized by the copyright owner, its agent, or the law.</li>
              <li>A statement that the information provided in the notification is accurate and that you are the copyright or intellectual property owner or authorized to act on behalf of the owner.</li>
            </ul>
            <p>
              To submit your infringement claim, please contact us at:{" "}
              <a href="mailto:feedback@rareprob.com" className="text-blue-600 hover:underline">
                feedback@rareprob.com
              </a>
              .
            </p>
          </section>

          {/* Section 15 - In-app Purchases */}
          <section id="section-15" className="mt-12">
            <h2 className="font-bold text-3xl text-gray-800 mb-4">In-app Purchases</h2>
            <p className="mb-4">
              We offer you choices with in-app purchases to improve your user experience and let you explore and take advantage of more features across the platform.
            </p>
            <p className="mb-4">
              If you wish to make any purchase, you do so through the Google Play service and also agree to its respective Terms and Conditions.{" "}
              <a href="https://play.google.com/intl/en-us/about/play-terms/index.html" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                https://play.google.com/intl/en-us/about/play-terms/index.html
              </a>
            </p>
            <p className="mb-4">Also, it is to be noted that we&apos;re not a party to any In-app purchases.</p>
            <p>
              When you make an in-app purchase, your purchase history and subscription status are recorded locally on your device via Google Play Billing. We do not store payment card details. Purchase history is used solely to validate and restore your premium access.
            </p>
          </section>

          {/* Section 16 - Liability Limitation */}
          <section id="section-16" className="mt-12">
            <h2 className="font-bold text-3xl text-gray-800 mb-4">Liability Limitation</h2>
            <p className="mb-4">
              ASD Dev Video Player for All Format shall in no event be liable for any loss or damage (incidental, or consequential) of your device or files, including photos, videos, documents, and music, caused by:
            </p>
            <ul className="list-disc list-inside ml-4 space-y-1">
              <li>Access or inability to access the services offered in our apps.</li>
              <li>Any third party&apos;s conduct.</li>
              <li>Unauthorized or inappropriate use of our services in mobile applications other than what they were intended for.</li>
            </ul>
          </section>

          {/* Section 17 - Disclaimer of Warranty */}
          <section id="section-17" className="mt-12">
            <h2 className="font-bold text-3xl text-gray-800 mb-4">Disclaimer of Warranty</h2>
            <p className="mb-4">
              Services provided by us, through mobile applications, are provided "as is" and "as available," with no warranties (expressed or implied). It also includes, but is not limited to, warranties of quality, marketability, fitness of services for particular purposes, or non-infringement.
            </p>
            <p>You use our services at your own risk and only as permitted by law.</p>
          </section>

          {/* Section 18 - Copyright and Trademarks */}
          <section id="section-18" className="mt-12">
            <h2 className="font-bold text-3xl text-gray-800 mb-4">Copyright and Trademarks</h2>
            <p className="mb-4">
              Our apps give users the ability to create their own content. We will never claim ownership of your original work. You alone remain the sole person responsible for every piece of content you create, and therefore, you warrant it.
            </p>
            <p className="mb-4">
              We own and retain all rights to its content, applications, and services. Whereas our service and content are protected by copyright, trademark, patent, trade secret, and other laws.
            </p>
            <p>
              We also reserve the right to use your feedback (without revealing any personal information about the person who provided it) as we see fit. Under no circumstances can the sender of any particular feedback claim ownership of it or ask for payment in return.
            </p>
          </section>

          {/* Section 19 - Push Notifications */}
          <section id="section-19" className="mt-12">
            <h2 className="font-bold text-3xl text-gray-800 mb-4">Push Notifications</h2>
            <p className="mb-4">
              We provide you with the choice to receive push notifications to inform you of built-in features of the Application or any additional use of the Platform, or you can say enabling In-app notifications is an alternative. If you turn off notifications, you might not get information right away, and you can miss out on the latest updates and information.
            </p>
            <h2 className="font-bold text-3xl text-gray-800 mb-4">Termination</h2>
            <p>
              Your access to any particular feature or the entire platform may be terminated if any violation is discovered in any way. The platform may be terminated with or without prior warning, and if, for any reason, you wish to do so, you may do so by deleting the application from your device.
            </p>
          </section>

          {/* Section 20 - Retention of data */}
          <section id="section-20" className="mt-12">
            <h2 className="font-bold text-3xl text-gray-800 mb-4">Retention of data</h2>
            <p className="mb-4">
              We will retain your Personal Data only for as long as is necessary for the purposes set out in this Privacy Policy. We will retain and use your Personal Data to the extent necessary to comply with our legal obligations (for example, if we are required to retain your data to comply with applicable laws), resolve disputes, and enforce our legal agreements and policies.
            </p>
            <p>
              We will also retain Usage Data for internal analysis purposes. Usage Data is generally retained for a shorter period of time, except when this data is used to strengthen the security or to improve the functionality of our Service, or we are legally obligated to retain this data for longer periods.
            </p>
          </section>

          {/* Section 21 - Data Recovery Policy */}
          <section id="section-21" className="mt-12">
            <h2 className="font-bold text-3xl text-gray-800 mb-4">Data Recovery Policy</h2>
            <p className="mb-4">
              Our apps do not support Recoverable action after uninstallation of the app. If users are using the Hider functionality, then it is crucial to remember that you may lose all the trash or hidden data if you clear all your data or factory reset your device.
            </p>
            <p className="mb-4">
              To prevent your data from being lost, you may transfer the trash or hidden files from our app to your device because once you uninstall our app, you won&apos;t be able to recover those files. Our developers will not be liable for any of the private files or data.
            </p>
            <p>
              We prioritize your control over personalized data. You can easily manage and delete your local database, preferences, and logs directly from the app&apos;s settings, without the need to visit any external service or URL. This includes clearing stored information in the local database, resetting your saved preferences, and deleting any logs generated during app usage, such as activity or error logs. All data management actions can be performed entirely within the app, giving you full control over your data without reliance on external platforms. We encourage users to regularly review and manage their data based on personal preferences.
            </p>
          </section>

          {/* Section 22 - Security of data */}
          <section id="section-22" className="mt-12">
            <h2 className="font-bold text-3xl text-gray-800 mb-4">Security of data</h2>
            <p className="mb-4">
              The security of your data is important to us, but remember that no method of transmission over the Internet or method of electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your Personal Data, we cannot guarantee its absolute security.
            </p>
            <p>
              We use industry-standard security measures, including device-based protections such as biometric authentication (fingerprint), to safeguard user data stored within the app. We do not collect any biometric data. It is processed locally on your device.
            </p>
          </section>

          {/* Section 23 - DO NOT TRACK */}
          <section id="section-23" className="mt-12">
            <h2 className="font-bold text-3xl text-gray-800 mb-4">OUR POLICY ON "DO NOT TRACK" SIGNALS UNDER THE CALIFORNIA ONLINE PROTECTION ACT (CALOPPA)</h2>
            <p className="mb-4">
              We do not support Do Not Track ("DNT"). Do Not Track is a preference you can set in your web browser to inform websites that you do not want to be tracked.
            </p>
            <p>
              You can enable or disable Do Not Track by visiting the Preferences or Settings page of your web browser.
            </p>
          </section>

          {/* Section 24 - Third-Party Services */}
          <section id="section-24" className="mt-12">
            <h2 className="font-bold text-3xl text-gray-800 mb-4">Third-Party Services</h2>
            <p>
              We use trusted third-party providers to support our app, such as for analytics and performance. They only access the data needed to perform their services and must keep it secure and confidential.
            </p>
          </section>

          {/* Section 24-1 - Disclaimer */}
          <section id="section-24-1" className="mt-12">
            <h2 className="font-bold text-3xl text-gray-800 mb-4">DISCLAIMER</h2>
            <p className="mb-4">
              Our apps are not associated with any social media platform. Any unauthorized acts and/or violations of intellectual property rights are solely the user&apos;s responsibility. Before downloading and using the content, we suggest that you seek the permission of the owner.
            </p>
            <h3 className="font-semibold text-xl text-gray-800 mb-2">1. AI Features – Image Processing</h3>
            <p className="mb-2">Our app uses Google&apos;s ML Kit and MediaPipe to power certain AI-based image editing features. Specifically, the app uses the following on-device capabilities:</p>
            <ul className="list-disc list-inside ml-4 space-y-1 mb-4">
              <li>Face Detection — Detects faces within images to enable features such as auto-framing or smart cropping.</li>
              <li>Selfie &amp; Subject Segmentation — Separates the subject (person or object) from the background in images to enable background removal or replacement features.</li>
            </ul>
            <p className="mb-2">Important disclosures regarding these features:</p>
            <ul className="list-disc list-inside ml-4 space-y-1 mb-4">
              <li>All processing is performed entirely on your device using Google ML Kit and MediaPipe. No facial data, biometric identifiers, or segmented images are sent to our servers or any third party.</li>
              <li>We do not store, share, or use any facial data for identification, profiling, or advertising purposes.</li>
              <li>These features only activate when you explicitly use them within the app.</li>
              <li>No facial recognition or biometric identification is performed — the app only detects the presence and position of faces to improve image composition.</li>
            </ul>
            <p className="mb-4">
              If you choose to use our AI-based image-to-image generation or editing features, the app may temporarily process your selected images locally or via secure servers strictly for the purpose of generating results. These images are not stored or shared after processing unless you explicitly save them on your device.
            </p>
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
              <p className="font-semibold text-gray-800 mb-1">Note:</p>
              <p className="text-base">
                Our app uses on-device face detection and image segmentation via Google ML Kit solely to enhance image editing features. This processing happens locally on your device and does not constitute biometric identification. No facial data is stored, transmitted, or shared.
              </p>
            </div>
          </section>

          {/* Section 24-1-1 - Analytics */}
          <section id="section-24-1-1" className="mt-12">
            <h2 className="font-bold text-3xl text-gray-800 mb-4">Analytics</h2>
            <p className="mb-4">
              We may use third-party Service Providers to monitor and analyze the use of our Service. Analytics is limited to non-personal data.
            </p>

            <div className="ml-2 space-y-6">
              <div>
                <h3 className="font-semibold text-xl text-gray-800 mb-2">1. Facebook Analytics (by Facebook Inc.)</h3>
                <p className="mb-2">We use Facebook Analytics for in-game data analytics to improve our games.</p>
                <p>
                  For more information on the privacy practices of Facebook, please visit Facebook&apos;s Data Policy:{" "}
                  <a href="https://www.facebook.com/privacy/explanation" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    https://www.facebook.com/privacy/explanation
                  </a>
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-xl text-gray-800 mb-2">2. Firebase Services (by Google LLC)</h3>
                <p className="mb-2">
                  Our app uses multiple Firebase services provided by Google LLC. Each service is described below. If you need more details regarding Firebase Services, feel free to visit:{" "}
                  <a href="https://firebase.google.com/support/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    https://firebase.google.com/support/privacy
                  </a>
                </p>

                <div className="ml-2 space-y-4 mt-4">
                  <div>
                    <h4 className="font-semibold text-lg text-gray-800 mb-1">2.1 Firebase Analytics</h4>
                    <p className="mb-2">Firebase Analytics helps us improve features, fix issues, and make better product decisions.</p>
                    <p className="mb-2">Data collected through Firebase Analytics may be transmitted to and processed on Google servers, which constitutes data sharing with a third-party service provider.</p>
                    <p className="mb-2">Data collected includes:</p>
                    <ul className="list-disc list-inside ml-4 space-y-1 mb-2">
                      <li>App events (e.g., app open, screen views, button taps, feature usage)</li>
                      <li>Device information (model, OS version, language, country)</li>
                      <li>App version and installation source</li>
                      <li>Session duration and frequency of use</li>
                      <li>Advertising ID (AAID on Android / IDFA on iOS)</li>
                      <li>Crash and ANR (App Not Responding) events</li>
                    </ul>
                    <p className="mb-2">Important disclosures:</p>
                    <ul className="list-disc list-inside ml-4 space-y-1 mb-2">
                      <li>Firebase Analytics data is pseudonymized, which means it is associated with a device identifier, not your name or personal identity.</li>
                      <li>We do not use Firebase Analytics to access your photos, videos, or any personal files.</li>
                      <li>Analytics data may be linked with other Firebase services, such as Remote Config and Crashlytics, for a unified view of app performance.</li>
                    </ul>
                    <p>
                      You can opt out of Firebase Analytics data collection at any time by disabling ad personalization in your device settings or by resetting your Advertising ID. On Android, go to Settings → Google → Ads → Reset Advertising ID or Opt Out of Ads Personalization.
                    </p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg text-gray-800 mb-1">2.2 Firebase Crashlytics</h4>
                    <p className="mb-2">We use Firebase Crashlytics to automatically detect, log, and report app crashes and stability issues.</p>
                    <p className="mb-2">This allows our team to identify and fix bugs quickly to improve your experience.</p>
                    <p className="mb-2">Data collected includes:</p>
                    <ul className="list-disc list-inside ml-4 space-y-1 mb-2">
                      <li>Crash stack traces (technical logs showing where the app failed)</li>
                      <li>Device state at the time of the crash (OS version, device model, available memory)</li>
                      <li>App version and build number</li>
                      <li>A randomly generated Crashlytics Installation UUID to track crash frequency per device</li>
                      <li>Whether the app was in the foreground or background</li>
                    </ul>
                    <p className="mb-2">Important disclosures:</p>
                    <ul className="list-disc list-inside ml-4 space-y-1">
                      <li>Crashlytics does not collect your name, email address, photos, videos, or any personal content.</li>
                      <li>Crash logs are anonymized and contain only technical diagnostic information.</li>
                      <li>Logs are used solely for debugging and stability improvement and never for advertising or profiling.</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg text-gray-800 mb-1">2.3 Firebase Remote Config</h4>
                    <p className="mb-2">We use Firebase Remote Config to update certain app settings, features, and content remotely without requiring you to download a new version of the app. This allows us to roll out features gradually, run A/B tests, or make quick adjustments to the app experience.</p>
                    <p className="mb-2">Data collected includes:</p>
                    <ul className="list-disc list-inside ml-4 space-y-1 mb-2">
                      <li>Firebase Installation ID (to identify the app instance for targeted configuration delivery)</li>
                      <li>App version and platform information</li>
                      <li>User property segments (e.g., app version group, country) are used to determine which configuration to deliver</li>
                    </ul>
                    <p className="mb-2">Important disclosures:</p>
                    <ul className="list-disc list-inside ml-4 space-y-1">
                      <li>Remote Config does not collect personal content such as your photos, files, or browsing activity.</li>
                      <li>It does not enable us to access or modify anything on your device remotely. It only controls settings within our app.</li>
                      <li>Configuration data fetched from Firebase is stored locally on your device.</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg text-gray-800 mb-1">2.4 Firebase Cloud Messaging</h4>
                    <p className="mb-2">We use Firebase Cloud Messaging to send push notifications to your device. These notifications may include app updates, feature announcements, reminders, or promotional messages related to our app.</p>
                    <p className="mb-2">Data collected includes:</p>
                    <ul className="list-disc list-inside ml-4 space-y-1 mb-2">
                      <li>FCM Registration Token (a unique token assigned to your app installation, used to route notifications to your device)</li>
                      <li>Device platform and app version (to ensure notification compatibility)</li>
                    </ul>
                    <p className="mb-2">Important disclosures:</p>
                    <ul className="list-disc list-inside ml-4 space-y-1">
                      <li>The FCM token is a technical identifier — it does not contain your name, email, or any personal identity information.</li>
                      <li>We do not use FCM to collect data about your notification interactions beyond what is necessary to deliver the notification.</li>
                      <li>You can disable push notifications at any time through your device settings.</li>
                      <li>On Android 13 and above, we request your explicit permission before sending any notifications.</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg text-gray-800 mb-1">2.5 Firebase Performance Monitoring</h4>
                    <p className="mb-2">We use Firebase Performance Monitoring to measure and improve the technical performance of our app. This SDK automatically collects data such as app startup time, screen rendering performance, network request response times, and crash traces.</p>
                    <p className="mb-2">Data collected includes:</p>
                    <ul className="list-disc list-inside ml-4 space-y-1 mb-2">
                      <li>App version and device information (model, OS version)</li>
                      <li>Network request URLs, response codes, and payload sizes</li>
                      <li>Screen rendering frame rates</li>
                      <li>App start-up duration</li>
                    </ul>
                    <p>This data is used solely to diagnose and fix performance issues. It does not include personal content such as photos, videos, or user-generated files.</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg text-gray-800 mb-1">2.6 Firebase In-App Messaging</h4>
                    <p className="mb-2">We use Firebase In-App Messaging to display contextual messages, tips, or promotional content to users who are actively using the app. These messages may include feature announcements, tips, or promotional offers.</p>
                    <p className="mb-2">Data collected includes:</p>
                    <ul className="list-disc list-inside ml-4 space-y-1 mb-2">
                      <li>Firebase Installation ID (to identify the app instance)</li>
                      <li>App engagement data (to determine when and to whom messages are shown)</li>
                    </ul>
                    <p>No personal content from your device is accessed. Messages are triggered based on in-app behavior (e.g., opening a feature) and not based on personal identity.</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg text-gray-800 mb-1">2.7 Firebase Realtime Database</h4>
                    <p>We use Firebase Realtime Database to store and sync app data in real time. This may include app configuration data, user preferences, or feature-related data necessary for the app to function correctly. Data stored in the database is protected by Firebase&apos;s security rules and is only accessible to authorized app functions. We do not store sensitive personal information such as payment details, precise location, or biometric data in this database.</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg text-gray-800 mb-1">2.8 Firebase AI / Gemini API</h4>
                    <p className="mb-2">We use Firebase AI (powered by Google&apos;s Gemini models) to enable AI-powered features within the app, such as image editing, content generation, or intelligent suggestions.</p>
                    <p className="mb-2">When you use an AI-powered feature:</p>
                    <ul className="list-disc list-inside ml-4 space-y-1 mb-2">
                      <li>Your input (such as a selected image or text prompt) may be temporarily processed by Google&apos;s AI infrastructure to generate a result.</li>
                      <li>Inputs are not used to train Google&apos;s AI models without your consent.</li>
                      <li>Processed data is not retained by Google beyond the immediate session unless explicitly stated.</li>
                    </ul>
                    <p>
                      For more information on how Google handles AI data, visit:{" "}
                      <a href="https://ai.google.dev/gemini-api/terms" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                        https://ai.google.dev/gemini-api/terms
                      </a>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Section 24-1-2 - Advertising */}
          <section id="section-24-1-2" className="mt-12">
            <h2 className="font-bold text-3xl text-gray-800 mb-4">Advertising</h2>
            <p className="mb-4">
              We reserve the right to place adverts on the platforms in order to maintain the free functionalities. These advertisements may be interspersed with other features, and some of those features may only be activated if you view the ads. If you choose to stop seeing certain adverts, you might lose access to those features or encounter other app restrictions.
            </p>
            <p className="mb-4">
              Your use of the App may be terminated if you use any method to get around any In-app advertisements.
            </p>
            <p className="mb-4">
              Before displaying any personalized advertisements, we use Google&apos;s User Messaging Platform (UMP) to request your consent in accordance with applicable privacy laws. You may update your ad consent preferences at any time through the app&apos;s settings.
            </p>
            <p className="mb-4">
              Our app uses Android&apos;s Privacy Sandbox APIs, including the Topics API, Custom Audiences API, and Attribution API. These APIs are provided by Google and are used to serve interest-based advertisements and measure ad performance without relying on cross-app identifiers. Data processed through these APIs is handled in accordance with Google&apos;s Privacy Sandbox policies. You can opt out through your device&apos;s Ad Privacy settings under Settings → Google → Ads.
            </p>

            <div className="ml-2 space-y-6">
              <div>
                <h3 className="font-semibold text-xl text-gray-800 mb-2">1. Google User Messaging Platform / Funding Choices (by Google LLC)</h3>
                <p className="mb-2">
                  Our app uses Google&apos;s User Messaging Platform (UMP) SDK to manage advertising consent in compliance with applicable privacy regulations including GDPR, CCPA, and India&apos;s DPDP Act.
                </p>
                <p className="mb-2">
                  When you first open the app (or when regulations require), UMP displays a consent dialog asking whether you agree to personalized advertising. Your response is recorded and transmitted to Google&apos;s Funding Choices service, which then communicates your consent signal to our advertising partners.
                </p>
                <p className="mb-2">What this SDK does:</p>
                <ul className="list-disc list-inside ml-4 space-y-1 mb-2">
                  <li>Displays consent dialogs required by GDPR (for EU/UK users), CCPA (for California users), and other applicable laws.</li>
                  <li>Stores your consent preference on your device.</li>
                  <li>Transmits your consent signal to Google and our ad network partners so they can serve ads in accordance with your choice.</li>
                  <li>Determines which ad experience you receive — personalized or non-personalized — based on your response.</li>
                </ul>
                <p className="mb-2">Data involved:</p>
                <ul className="list-disc list-inside ml-4 space-y-1 mb-2">
                  <li>Your consent choice (yes/no to personalized ads)</li>
                  <li>Your approximate region/jurisdiction (to determine which consent rules apply)</li>
                  <li>A timestamp of when consent was given or denied</li>
                </ul>
                <p className="mb-2">No sensitive personal data is collected by this SDK. It acts solely as a consent management mechanism.</p>
                <p className="mb-2">
                  For more information, visit Google&apos;s Privacy Policy:{" "}
                  <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    https://policies.google.com/privacy
                  </a>
                </p>
                <p>
                  For Funding Choices documentation:{" "}
                  <a href="https://support.google.com/fundingchoices" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    https://support.google.com/fundingchoices
                  </a>
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-xl text-gray-800 mb-2">2. Facebook Audience Network (by Facebook Inc.)</h3>
                <p>
                  For more information about Facebook Audience Network, please visit their Policy:{" "}
                  <a href="https://developers.facebook.com/docs/audience-network/optimization/best-practices/an-policy/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    https://developers.facebook.com/docs/audience-network/optimization/best-practices/an-policy/
                  </a>
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-xl text-gray-800 mb-2">3. Facebook Marketing (part of Meta Business)</h3>
                <p>
                  The purpose of this SDK is Analytics, ad attribution, and personalized advertising. It collects device information, advertising ID, app events, and usage data. For more details, you can check their privacy policy:{" "}
                  <a href="https://www.facebook.com/privacy/policy/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    https://www.facebook.com/privacy/policy/
                  </a>
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-xl text-gray-800 mb-2">4. AdMob (by Google Inc.)</h3>
                <p className="mb-2">
                  Understand how "the end-user data collected by Google Mobile Ads SDK" from the link below:{" "}
                  <a href="https://developers.google.com/admob/android/privacy/play-data-disclosure" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    https://developers.google.com/admob/android/privacy/play-data-disclosure
                  </a>
                </p>
                <p className="mb-2">
                  You can opt-out from the AdMob by Google service by following the instructions described by Google:{" "}
                  <a href="https://support.google.com/My-Ad-Center-Help/answer/12155764?hl=en" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    https://support.google.com/My-Ad-Center-Help/answer/12155764?hl=en
                  </a>
                </p>
                <p>
                  For more information on how Google uses the collected information, please visit:{" "}
                  <a href="https://policies.google.com/technologies/partner-sites" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    https://policies.google.com/technologies/partner-sites
                  </a>{" "}
                  or visit the Privacy Policy of Google:{" "}
                  <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    https://policies.google.com/privacy
                  </a>
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-xl text-gray-800 mb-2">5. AppLovin (by AppLovin Corporation)</h3>
                <p className="mb-2">
                  If you&apos;d like to opt out of AppLovin service, please visit the AppLovin Opt-out page:{" "}
                  <a href="https://dash.applovin.com/p/how-applovin-shows-you-ads" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    https://dash.applovin.com/p/how-applovin-shows-you-ads
                  </a>
                </p>
                <p>
                  If you&apos;d like to learn more about AppLovin, please visit the Privacy Policy page of AppLovin:{" "}
                  <a href="https://www.applovin.com/privacy/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    https://www.applovin.com/privacy/
                  </a>
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-xl text-gray-800 mb-2">6. Pangle (by Bytedance Pte.Ltd.)</h3>
                <p>
                  For more information about Pangle, please visit their Privacy Policy:{" "}
                  <a href="https://ad.oceanengine.com/union/media/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    https://ad.oceanengine.com/union/media/privacy
                  </a>
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-xl text-gray-800 mb-2">7. Outbrain (by Teads Holding Co.)</h3>
                <p className="mb-2">
                  Outbrain is a content recommendation and native advertising network. We use it to display sponsored content recommendations in the app to generate revenue that supports our free features.
                </p>
                <p className="mb-2">
                  Data collected includes unique user identifier (UUID), IP address (truncated to mask identifying information), mobile advertising IDs (such as IDFA or AAID), and content interaction data to serve recommendations based on inferred user interests. It does not conduct cross-device tracking and cannot link a user interacting on their phone to the same user on another device.
                </p>
                <p>
                  For more details, please check:{" "}
                  <a href="https://www.outbrain.com/privacy/user-policy/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    https://www.outbrain.com/privacy/user-policy/
                  </a>
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-xl text-gray-800 mb-2">8. Teads (by Teads Inc.)</h3>
                <p className="mb-2">
                  Teads is an outstream video and display advertising platform. We use it to serve video ad formats within the app, enabling monetization through high-quality video placements.
                </p>
                <p className="mb-2">
                  The data it collects includes device IDs, IP addresses, browsing history (content viewed), product interactions (such as app launches, taps, and clicks), and advertising data, such as information about ads the user has seen. Teads does not knowingly collect or process special categories of sensitive data.
                </p>
                <p>
                  To opt out of its advertising services or for more details, please check:{" "}
                  <a href="https://privacy-policy.teads.com/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    https://privacy-policy.teads.com/
                  </a>
                </p>
                <p className="mt-2">For GDPR or CCPA requests, contact dpo@teads.com.</p>
              </div>

              <div>
                <h3 className="font-semibold text-xl text-gray-800 mb-2">9. Zemanta (Outbrain DSP)</h3>
                <p className="mb-2">
                  We use it to enable programmatic ad buying that automatically matches the most relevant ads to users in real time.
                </p>
                <p className="mb-2">
                  Advertising IDs (like IDFA), which are unique IDs associated with individual mobile devices, are used to build a user profile. Users can reset these IDs by instructing their device to do so. It also collects IP addresses, device identifiers, and browsing/interaction signals for ad targeting purposes.
                </p>
                <p className="mb-2">
                  For more information:{" "}
                  <a href="https://www.outbrain.com/privacy/privacy-policy-outbrain-dsp/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    https://www.outbrain.com/privacy/privacy-policy-outbrain-dsp/
                  </a>
                </p>
                <p>
                  To opt-out:{" "}
                  <a href="https://www.zemanta.com/opt-out/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    https://www.zemanta.com/opt-out/
                  </a>
                </p>
                <p>
                  And, to check their advertising guidelines, check:{" "}
                  <a href="https://www.zemanta.com/advertising-guidelines/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    https://www.zemanta.com/advertising-guidelines/
                  </a>
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-xl text-gray-800 mb-2">10. Xandr / AppNexus (by Microsoft)</h3>
                <p className="mb-2">
                  We use it to access a broad marketplace of advertisers through real-time bidding, helping us maximize ad revenue while maintaining app quality.
                </p>
                <p className="mb-2">
                  Xandr enables advertising purposes through the use of non-personally identifiable information, which includes browser type, cookie ID, IP address, device identifiers, web pages or apps visited or used, and date and time that web pages or apps were visited. Xandr&apos;s mobile SDKs include client support for the Global Privacy Platform (GPP), GDPR, CCPA, and the Digital Services Act (DSA).
                </p>
                <p className="mb-2">
                  For more details:{" "}
                  <a href="https://help.ads.microsoft.com/#apex/ads/en/ext60205" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    https://help.ads.microsoft.com/#apex/ads/en/ext60205
                  </a>
                </p>
                <p>
                  To opt out of personalized advertising from Xandr (Microsoft), visit Microsoft&apos;s Privacy Statement at{" "}
                  <a href="https://privacy.microsoft.com/en-us/privacystatement" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    https://privacy.microsoft.com/en-us/privacystatement
                  </a>{" "}
                  and navigate to the ad settings section. You can also opt out through your mobile device&apos;s advertising settings by limiting ad tracking on Android.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-xl text-gray-800 mb-2">11. Index Exchange / Casale Media (by Index Exchange Inc.)</h3>
                <p className="mb-2">
                  Index Exchange (formerly Casale Media) is a supply-side advertising exchange. We use it to make our app&apos;s ad inventory available to a wider pool of buyers through programmatic auctions, increasing competition for ad slots and maximizing revenue.
                </p>
                <p className="mb-2">It collects cookies for the purpose of tracking when a user has seen an advertisement.</p>
                <p className="mb-2">
                  These cookies do not provide personally identifiable information. They are used to inform the exchange whether an advertisement has been served to the same device before, and to facilitate retargeting at a campaign level. Unique user identification numbers are deleted after 90 days of user inactivity and are not tied to any personally identifiable information.
                </p>
                <p className="mb-2">
                  For more details:{" "}
                  <a href="https://www.indexexchange.com/privacy/exchange-platform-privacy-policy/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    https://www.indexexchange.com/privacy/exchange-platform-privacy-policy/
                  </a>
                </p>
                <p>
                  And, you can opt out of targeted advertising through the NAI opt-out tool at{" "}
                  <a href="https://optout.networkadvertising.org/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    https://optout.networkadvertising.org/
                  </a>{" "}
                  or by adjusting your mobile device&apos;s advertising ID settings.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-xl text-gray-800 mb-2">12. CHEQ / CheqZone (by CHEQ AI Technologies)</h3>
                <p className="mb-2">
                  We use it to protect our advertising ecosystem from invalid traffic, bot activity, and click fraud, to ensure our ad partners receive genuine impressions and to protect our app&apos;s revenue integrity.
                </p>
                <p className="mb-2">
                  CHEQ uses the IP address to determine whether a session is fraudulent. This information is considered personal data under GDPR. CHEQ processes personal data only as necessary to provide its fraud detection services, and data collected is used only for this purpose. CHEQ does not use cookies to track users across websites or for any advertising or targeting purposes. Its cookies are top-level domain cookies used solely to block fraudulent traffic.
                </p>
                <p className="mb-2">
                  For more details:{" "}
                  <a href="https://cheq.ai/data-privacy/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    https://cheq.ai/data-privacy/
                  </a>
                </p>
                <p>Users can contact CHEQ directly at privacy@cheq.ai for data subject requests or if they want to opt out of its services.</p>
              </div>

              <div>
                <h3 className="font-semibold text-xl text-gray-800 mb-2">13. Vungle (by Vungle Inc.)</h3>
                <p className="mb-2">
                  You can opt out of Vungle service by following the instructions as described by Vungle on their Privacy Policy page:{" "}
                  <a href="https://vungle.com/privacy/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    https://vungle.com/privacy/
                  </a>
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-xl text-gray-800 mb-2">14. Mintegral (by Mintegral International Ltd.)</h3>
                <p>
                  For more information about Mintegral, please visit their Privacy Policy:{" "}
                  <a href="https://www.mintegral.com/en/privacy/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    https://www.mintegral.com/en/privacy/
                  </a>
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-xl text-gray-800 mb-2">15. Unity Ads (by Unity)</h3>
                <p>
                  For more information about Unity Ads, please visit their Privacy Policy:{" "}
                  <a href="https://unity.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    https://unity.com/legal/privacy-policy
                  </a>
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-xl text-gray-800 mb-2">16. InMobi (by InMobi Pvt. Ltd.)</h3>
                <p className="mb-2">
                  You can opt out of the InMobi service by following the instructions as described on the InMobi Opt-out page:{" "}
                  <a href="https://advertising.inmobi.com/page/opt-out" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    https://advertising.inmobi.com/page/opt-out
                  </a>
                </p>
                <p>
                  For more information on the privacy practices and policies of InMobi, please visit the InMobi Privacy Policy:{" "}
                  <a href="https://advertising.inmobi.com/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    https://advertising.inmobi.com/privacy-policy
                  </a>
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-xl text-gray-800 mb-2">17. Google AdSense &amp; DoubleClick Cookie</h3>
                <p className="mb-2">
                  Google, as a third-party vendor, uses cookies to serve ads on our Service. Google&apos;s use of the DoubleClick cookie enables it and its partners to serve ads to our users based on their visit to our Service or other websites on the Internet.
                </p>
                <p>
                  You may opt out of the use of the DoubleClick Cookie for interest-based advertising by visiting the Google Ads Settings web page:{" "}
                  <a href="https://myadcenter.google.com/home?hl=en&sasb=true&ref=ad-settings" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    https://myadcenter.google.com/home?hl=en&sasb=true&ref=ad-settings
                  </a>
                </p>
              </div>
            </div>
          </section>

          {/* Section 24-1-3 - Behavioral Remarketing */}
          <section id="section-24-1-3" className="mt-12">
            <h2 className="font-bold text-3xl text-gray-800 mb-4">Behavioral Remarketing</h2>
            <p className="mb-4">
              We use remarketing services to advertise on third-party websites to you after you visit our Service. Our third-party vendors, and we use cookies to inform, optimize, and serve ads based on your past visits to our Service.
            </p>

            <div className="ml-2 space-y-6">
              <div>
                <h3 className="font-semibold text-xl text-gray-800 mb-2">1. Google Ads</h3>
                <p className="mb-2">Google AdWords remarketing service is provided by Google Inc.</p>
                <p className="mb-2">
                  You can opt out of Google Analytics for Display Advertising and customize the Google Display Network ads by visiting the Google Ads Settings page:{" "}
                  <a href="https://myadcenter.google.com/home?hl=en&sasb=true&ref=ad-settings" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    https://myadcenter.google.com/home?hl=en&sasb=true&ref=ad-settings
                  </a>
                </p>
                <p className="mb-2">
                  Google also recommends installing the Google Analytics Opt-out Browser Add-on:{" "}
                  <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    https://tools.google.com/dlpage/gaoptout
                  </a>
                </p>
                <p>
                  For more information on the privacy practices of Google, please visit:{" "}
                  <a href="https://policies.google.com/privacy?hl=en" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    https://policies.google.com/privacy?hl=en
                  </a>
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-xl text-gray-800 mb-2">2. Facebook Ads</h3>
                <p className="mb-2">Facebook remarketing service is provided by Facebook Inc.</p>
                <p className="mb-2">
                  You can learn more about interest-based advertising from Facebook by visiting this page:{" "}
                  <a href="https://www.facebook.com/business/help/488043719226449?id=434838534925385" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    https://www.facebook.com/business/help/488043719226449?id=434838534925385
                  </a>
                </p>
                <p className="mb-2">
                  To opt out of Facebook&apos;s interest-based ads, follow these instructions from Facebook:{" "}
                  <a href="https://www.facebook.com/help/568137493302217" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    https://www.facebook.com/help/568137493302217
                  </a>
                </p>
                <p className="mb-2">
                  Facebook adheres to the Self-Regulatory Principles for Online Behavioural Advertising established by the Digital Advertising Alliance. You can also opt-out from Facebook and other participating companies through the Digital Advertising Alliance in the USA{" "}
                  <a href="https://optout.aboutads.info/?c=2&lang=EN" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    https://optout.aboutads.info/?c=2&lang=EN
                  </a>
                  , or the European Interactive Digital Advertising Alliance in Europe{" "}
                  <a href="https://www.youronlinechoices.eu/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    https://www.youronlinechoices.eu/
                  </a>
                  , or opt-out using your mobile device settings.
                </p>
                <p>
                  For more information on the privacy practices of Facebook, please visit Facebook&apos;s Data Policy:{" "}
                  <a href="https://www.facebook.com/privacy/policy/?entry_point=data_policy_redirect&entry=0" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    https://www.facebook.com/privacy/policy/
                  </a>
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-xl text-gray-800 mb-2">3. AppLovin (by AppLovin Corporation)</h3>
                <p className="mb-2">
                  If you&apos;d like to opt out of AppLovin service, please visit the AppLovin Opt-out page:{" "}
                  <a href="https://dash.applovin.com/p/how-applovin-shows-you-ads" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    https://dash.applovin.com/p/how-applovin-shows-you-ads
                  </a>
                </p>
                <p>
                  If you&apos;d like to learn more about AppLovin, please visit the Privacy Policy page of AppLovin:{" "}
                  <a href="https://www.applovin.com/privacy/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    https://www.applovin.com/privacy/
                  </a>
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-xl text-gray-800 mb-2">4. Other Social Media Platforms</h3>
                <p className="mb-4">
                  We may use your information to send you marketing and promotional communications about our apps and services. This includes updates, special offers, and news related to our products. You may receive these communications through various channels, including email, push notifications, or in-app messaging.
                </p>
                <p className="mb-2">To promote our apps and engage with users, we also use a variety of social media platforms such as:</p>

                <div className="ml-4 space-y-3 text-base">
                  <div>
                    <p className="font-medium text-gray-800">Facebook</p>
                    <p>Rareprob – <a href="https://www.facebook.com/rareprob" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">https://www.facebook.com/rareprob</a></p>
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">Instagram</p>
                    <p>Rareprob – <a href="https://www.instagram.com/rareprob_/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">https://www.instagram.com/rareprob_/</a></p>
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">X (formerly Twitter)</p>
                    <p>Rareprob – <a href="https://x.com/rareprob" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">https://x.com/rareprob</a></p>
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">LinkedIn</p>
                    <p>Rareprob – <a href="https://www.linkedin.com/company/rareprob/mycompany/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">https://www.linkedin.com/company/rareprob/mycompany/</a></p>
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">YouTube</p>
                    <p>Rareprob – <a href="https://www.youtube.com/channel/UCNXZXijMtxueoWaNiye73AQ" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">https://www.youtube.com/channel/UCNXZXijMtxueoWaNiye73AQ</a></p>
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">Pinterest</p>
                    <p>Rareprob – <a href="https://pin.it/UfXhnCWtj" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">https://pin.it/UfXhnCWtj</a></p>
                  </div>
                </div>

                <p className="mt-4">
                  These platforms allow us to share updates, features, and promotional content, and interact with users who follow our accounts. Please note that while we use these platforms to advertise and promote our apps, we do not share your personal information with these social networks unless you specifically opt in or interact with our content directly on their platforms.
                </p>
                <p className="mt-2">
                  If you do not wish to receive marketing communications, you can opt-out by following the unsubscribe instructions provided in any email or contacting us directly.
                </p>
              </div>
            </div>
          </section>

          {/* Section 25 - Payments */}
          <section id="section-25" className="mt-12">
            <h2 className="font-bold text-3xl text-gray-800 mb-4">Payments</h2>
            <p className="mb-4">
              We may provide paid products and/or services within the Service. In that case, we use third-party services for payment processing (e.g. payment processors).
            </p>
            <p className="mb-4">
              We will not store or collect your payment card details. That information is provided directly to our third-party payment processors whose use of your personal information is governed by their Privacy Policy. These payment processors adhere to the standards set by PCI-DSS as managed by the PCI Security Standards Council, which is a joint effort of brands like Visa, MasterCard, American Express and Discover. PCI-DSS requirements help ensure the secure handling of payment information.
            </p>
            <p className="mb-2">The payment processors we work with are:</p>
            <p className="mb-1 font-medium text-gray-800">Google Play In-App Payments</p>
            <p>
              Their Privacy Policy can be viewed at{" "}
              <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                https://policies.google.com/privacy
              </a>
            </p>
          </section>

          {/* Section 26 - Links to other sites */}
          <section id="section-26" className="mt-12">
            <h2 className="font-bold text-3xl text-gray-800 mb-4">Links to other sites</h2>
            <p>
              Our Service may contain links to other sites that are not operated by us. If you click a third-party link, you will be directed to that third party&apos;s site. We strongly advise you to review the Privacy Policy of every site you visit. We have no control over and assume no responsibility for the content, privacy policies, or practices of any third-party sites or services.
            </p>
          </section>

          {/* Section 27 - Children's Privacy */}
          <section id="section-27" className="mt-12">
            <h2 className="font-bold text-3xl text-gray-800 mb-4">Children&apos;s Privacy</h2>
            <p className="mb-4">
              Our products and services are designed for users who are 13 years of age or older (or the age specified by law in your country/region). If you are a guardian of a child under the age of 13 (or the legal age in your country/region), we encourage you to actively guide and supervise the child during their use of our products, ensuring the safety of their personal information.
            </p>
            <p className="mb-4">
              We advise child users under 13 (or the prescribed age in your country/region) to carefully read this policy with the assistance of a parent or guardian before using our services. All services or the provision of information should only be done with parental or guardian consent. If we discover that we have inadvertently collected personal information from a child, we will take swift action to delete the data.
            </p>

            <h3 className="font-semibold text-2xl text-gray-800 mt-8 mb-3">Sharing of Children&apos;s Personal Information</h3>
            <p className="mb-2">We are committed to protecting children&apos;s privacy.</p>
            <ul className="list-disc list-inside ml-4 space-y-1 mb-4">
              <li>We do not sell, rent, or share children&apos;s personal information with third parties for marketing or advertising purposes.</li>
              <li>We do not allow third-party SDKs or ad networks to collect personal data from children for behavioral advertising.</li>
              <li>Any third-party services used in the app are strictly limited to supporting internal operations and ensuring app functionality (such as analytics, crash reporting, or hosting).</li>
              <li>These third parties are required to comply with COPPA (Children&apos;s Online Privacy Protection Act) and other applicable child data protection laws.</li>
            </ul>

            <h3 className="font-semibold text-2xl text-gray-800 mt-8 mb-3">Public Display and Sharing of Information by Children</h3>
            <p className="mb-2">Our apps are designed to be safe for children.</p>
            <ul className="list-disc list-inside ml-4 space-y-1 mb-4">
              <li>We do not allow children to publicly share or publish personal information (such as full name, address, phone number, or email).</li>
              <li>If the app includes features like usernames, leaderboards, or game profiles, these are designed to avoid the collection of personally identifiable information, prevent public exposure of sensitive data, and use anonymized or non-identifiable data only.</li>
              <li>We actively take steps to prevent accidental sharing of personal information by children.</li>
            </ul>

            <h3 className="font-semibold text-2xl text-gray-800 mt-8 mb-3">Parental and Caregiver Rights (Children&apos;s Data Rights)</h3>
            <p className="mb-4">
              In accordance with the Children&apos;s Online Privacy Protection Act (COPPA) and other global privacy regulations, parents and legal guardians have the following rights regarding their child&apos;s personal information.
            </p>
            <p className="mb-4">
              We clearly inform caregivers that they have the right to review, delete, and control the collection and use of their child&apos;s personal data.
            </p>
            <ul className="list-disc list-inside ml-4 space-y-2 mb-4">
              <li>
                <span className="font-medium">Right to Review Child&apos;s Data:</span> Parents or legal guardians have the right to request access to the personal information we may have collected from their child. Email us at{" "}
                <a href="mailto:feedback@rareprob.com" className="text-blue-600 hover:underline">feedback@rareprob.com</a>{" "}
                with subject line: &quot;Review Child Data&quot;. Provide necessary verification details. We will respond within a reasonable timeframe after verifying your identity.
              </li>
              <li>
                <span className="font-medium">Right to Delete Child&apos;s Data:</span> Parents have the right to request deletion of their child&apos;s personal information. Email us at{" "}
                <a href="mailto:feedback@rareprob.com" className="text-blue-600 hover:underline">feedback@rareprob.com</a>{" "}
                with subject line: &quot;Delete Child Data&quot;. Once verified, we will delete the data securely, and retention will only occur if required by law.
              </li>
              <li>
                <span className="font-medium">Right to Stop Data Collection or Use:</span> Parents can request that we stop collecting or using their child&apos;s data at any time by emailing{" "}
                <a href="mailto:feedback@rareprob.com" className="text-blue-600 hover:underline">feedback@rareprob.com</a>{" "}
                with subject: &quot;Stop Data Collection&quot;. After processing, we will stop collecting personal data, and some features of the app may no longer function properly.
              </li>
              <li>
                <span className="font-medium">Right to Withdraw Consent:</span> If consent was previously given, parents may withdraw it at any time, which will stop further data collection and trigger deletion where applicable.
              </li>
              <li>
                <span className="font-medium">Right to Know (Transparency Right):</span> Parents have the right to know what data is collected, why it is collected, how it is used, and whether it is shared with third parties.
              </li>
            </ul>

            <h3 className="font-semibold text-2xl text-gray-800 mt-8 mb-3">Verification Requirement</h3>
            <p className="mb-4">To protect children&apos;s privacy, we may require verification of identity before processing any request.</p>

            <h3 className="font-semibold text-2xl text-gray-800 mt-8 mb-3">Data Minimization and Retention</h3>
            <p className="mb-2">We follow strict data minimization principles to protect user privacy, especially for children.</p>
            <ul className="list-disc list-inside ml-4 space-y-1 mb-4">
              <li>We only collect necessary personal information (like Device type, model, operating system version, App version, performance data, crash logs, diagnostic information, Advertising ID, Usage and Analytics Data) and proportionate to provide and improve our app&apos;s core functionality.</li>
              <li>We do not collect excessive, unnecessary, or unrelated data.</li>
              <li>We ensure that data collection is limited to specific, explicit, and legitimate purposes.</li>
              <li>For child users, we do not require children to provide personal information to access basic features of the app.</li>
              <li>We avoid collecting personally identifiable information (PII) unless absolutely required for functionality.</li>
              <li>Where possible, we use anonymous data, aggregated data, and non-identifiable information.</li>
            </ul>

            <h3 className="font-semibold text-2xl text-gray-800 mt-8 mb-3">Default Privacy Settings (Privacy by Design)</h3>
            <p className="mb-4">
              Our app is designed with privacy by default, especially for children. Features are configured to limit data collection automatically without requiring user action. Any optional data collection is clearly disclosed and requires user or parental awareness where applicable.
            </p>

            <h3 className="font-semibold text-2xl text-gray-800 mt-8 mb-3">Data Retention Limitation</h3>
            <p className="mb-4">
              We retain personal data only for as long as necessary to fulfill the purposes outlined in this policy. We delete or anonymize data securely when it is no longer required. For children&apos;s data, we apply shorter retention periods and stricter safeguards.
            </p>

            <h3 className="font-semibold text-2xl text-gray-800 mt-8 mb-3">No Sensitive Data Collection</h3>
            <p className="mb-2">We do not knowingly collect sensitive personal information such as:</p>
            <ul className="list-disc list-inside ml-4 space-y-1 mb-4">
              <li>Precise location data</li>
              <li>Contact details (phone number, email) from children</li>
              <li>Biometric data</li>
              <li>Financial information</li>
            </ul>

            <h3 className="font-semibold text-2xl text-gray-800 mt-8 mb-3">Limited Access and Internal Controls</h3>
            <p className="mb-4">
              Access to data is restricted to authorized personnel only. We implement technical and organizational safeguards to prevent unauthorized access, misuse, or disclosure.
            </p>

            <h3 className="font-semibold text-2xl text-gray-800 mt-8 mb-3">No Profiling or Behavioral Tracking for Children</h3>
            <p>
              We do not profile children or track their behavior for advertising or marketing purposes. We do not create user profiles based on children&apos;s activity.
            </p>
          </section>

          {/* Section 28 - Changes to Privacy Policy */}
          <section id="section-28" className="mt-12">
            <h2 className="font-bold text-3xl text-gray-800 mb-4">Changes to this privacy policy</h2>
            <p className="mb-4">
              We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page. We will let you know via email and/or a prominent notice on our Service, prior to the change becoming effective, and update the "effective date" at the top of this Privacy Policy.
            </p>
            <p>
              You are advised to review this Privacy Policy periodically for any changes. Changes to this Privacy Policy are effective when they are posted on this page.
            </p>
          </section>

          {/* Section 29 - Contact Us */}
          <section id="section-29" className="mt-12 mb-16">
            <h2 className="font-bold text-3xl text-gray-800 mb-4">Contact us</h2>
            <p className="mb-2">
              If you have any questions or suggestions, please contact us at{" "}
              <a href="mailto:feedback@rareprob.com" className="text-blue-600 hover:underline">
                feedback@rareprob.com
              </a>
              . We are also open to your feedback.
            </p>
          </section>
        </div>

        <div className="pt-10">
          <NewFooter color="bg-[#6a1f9b]/90" />
        </div>
      </main>
    </>
  );
}
